// =========================================================================
// Agrupación de pólizas duplicadas por asegurado + producto + frecuencia
// =========================================================================
//
// Función pura que reduce una lista plana de pólizas a una lista de grupos.
// Se usa en el tab "Gestión de Pólizas" del insurance-console para colapsar
// los duplicados que aparecen cuando un mismo cliente intentó el flujo de
// compra varias veces con el mismo producto.
//
// Reglas:
//   - Clave de agrupación: cédula + nombre del producto + frecuencia +
//     código de aseguradora. (Aseguradoras distintas = grupos distintos.)
//   - Pólizas sin cédula no se agrupan (cada una queda como grupo de 1).
//   - Pólizas con 1 sola póliza por clave → quedan como grupo de 1 (isGroup
//     = false). El template las renderiza como rows normales sin marca.
//   - Tiebreaker para elegir la póliza primaria del grupo:
//       1) COMPLETED gana sobre cualquier otra.
//       2) Si no hay COMPLETED, el de mayor `funnelStep` del metadata.
//       3) Si empatan en funnelStep, el de fecha más reciente.
//
// Concepto "todo abandonado" (isAllAbandoned):
//   Un grupo es "todo abandonado" si NINGUNA póliza alcanzó la vía exitosa
//   del funnel (ISSUED → PAYMENT_RECORDED → COMPLETED → ACTIVE). ANNULLED
//   NO cuenta como exitoso aunque sea terminal. Esa marca se usa en la UI
//   para ocultar los intentos previos por default y dejar solo el primary
//   (el intento más reciente) con un badge "+N intentos previos".
//
// Limitación conocida: la agrupación es por página. Si el mismo grupo se
// distribuye en páginas distintas del backend, no se junta. Hay un banner
// caveat en la UI explicándolo.

import { getPolicyStatusMetadata } from './policy-status-metadata';

// Estados que representan la "vía exitosa" del funnel: una póliza que
// llegó al menos a ISSUED ya cuenta como esfuerzo concretado (el negocio
// dice "vendí = emitida"). ANNULLED queda fuera porque, aunque terminal,
// representa que el flujo NO se concretó como venta efectiva.
const SUCCESSFUL_PATH_STATUSES = new Set<string>([
  'ISSUED',
  'PAYMENT_RECORDED',
  'COMPLETED',
  'ACTIVE'
]);

export interface PolicyGroup {
  /** Clave única del grupo: 'cedula|product|frequency|insurer'.
   *  Pólizas sin cédula usan keys irrepetibles para que cada una sea su
   *  propio grupo de 1. */
  key: string;
  /** Cantidad de pólizas en el grupo (>= 1). */
  count: number;
  /** Póliza representante (la que se muestra colapsada). */
  primary: any;
  /** Las demás pólizas del grupo (sólo cuando count > 1). */
  others: any[];
  /** Marca para que el template decida si renderizar el chevron + badge. */
  isGroup: boolean;
  /** True si NINGUNA póliza del grupo alcanzó la vía exitosa (ISSUED+).
   *  La UI usa esta marca para mostrar solo el primary y badge
   *  "+N intentos previos" por default. */
  isAllAbandoned: boolean;
  /** Cantidad de intentos previos no exitosos que la UI puede ocultar
   *  por default cuando isAllAbandoned es true (= count - 1). */
  hiddenAttemptsCount: number;
}

// --- Accessors defensivos: el backend devuelve a veces los campos en
// `policy.params.X` y otras en `policy.X`. Leemos ambos con fallback. ----

function getIdentity(p: any): string {
  return (p?.params?.insured?.identity || p?.insured?.identity || '').toString().trim();
}

function getProductName(p: any): string {
  return (p?.params?.insurance_product?.name || p?.insurance_product?.name || '').toString().trim();
}

function getFrequency(p: any): string {
  return (p?.params?.frequency || p?.frequency || '').toString().trim();
}

function getInsurerCode(p: any): string {
  return (p?.params?.insurance?.code || p?.insurance?.code || '').toString().trim();
}

function getPolicyStatus(p: any): string {
  return (p?.params?.policy_status || p?.policy_status || '').toString().trim();
}

function getPolicyDate(p: any): number {
  // Para el tiebreaker. Convertimos cualquier fecha a timestamp; si no hay
  // ninguna, devolvemos 0 — quedan al final del orden.
  const d = p?.params?.update_date || p?.params?.issue_date || p?.date || p?.createdAt;
  return d ? new Date(d).getTime() : 0;
}

// Comparador para definir cuál póliza del grupo es la primaria.
// Devuelve negativo si `a` debería ir antes que `b`.
function comparePrimary(a: any, b: any): number {
  const sA = getPolicyStatus(a);
  const sB = getPolicyStatus(b);

  // 1) COMPLETED gana siempre.
  const aCompleted = sA === 'COMPLETED';
  const bCompleted = sB === 'COMPLETED';
  if (aCompleted && !bCompleted) return -1;
  if (bCompleted && !aCompleted) return 1;

  // 2) Mayor funnelStep gana (orden descendente).
  const stepA = getPolicyStatusMetadata(sA).funnelStep;
  const stepB = getPolicyStatusMetadata(sB).funnelStep;
  if (stepA !== stepB) return stepB - stepA;

  // 3) Fecha más reciente desempata.
  return getPolicyDate(b) - getPolicyDate(a);
}

/**
 * Agrupa pólizas por cédula + producto + frecuencia + aseguradora.
 * Devuelve grupos en el orden de primera aparición de su key en `policies`,
 * para preservar el orden que viene del backend en la vista.
 */
export function groupPoliciesByInsuredProduct(policies: any[]): PolicyGroup[] {
  if (!Array.isArray(policies) || policies.length === 0) return [];

  const buckets = new Map<string, any[]>();
  const order: string[] = [];

  policies.forEach((p, index) => {
    const identity = getIdentity(p);
    // Sin cédula → cada póliza queda en su propio grupo (key irrepetible).
    const key = identity
      ? `${identity}|${getProductName(p)}|${getFrequency(p)}|${getInsurerCode(p)}`
      : `__no_group__|${p?._id || index}`;

    if (!buckets.has(key)) {
      buckets.set(key, [p]);
      order.push(key);
    } else {
      buckets.get(key)!.push(p);
    }
  });

  return order.map(key => {
    const inGroup = buckets.get(key)!.slice().sort(comparePrimary);
    const primary = inGroup[0];
    const others = inGroup.slice(1);
    const isAllAbandoned = inGroup.every(
      p => !SUCCESSFUL_PATH_STATUSES.has(getPolicyStatus(p))
    );
    return {
      key,
      count: inGroup.length,
      primary,
      others,
      isGroup: inGroup.length > 1,
      isAllAbandoned,
      hiddenAttemptsCount: isAllAbandoned ? Math.max(0, inGroup.length - 1) : 0
    };
  });
}
